import { useQuery } from '@apollo/client';
import { getExtendQuery } from '@openx/components-targeting/Targeting/shared/InventoryAndContent/utils';
import { useTargetingContext } from '@openx/components-targeting/utils/context/TargetingContext';
import { ACL_TYPE } from '@openx/types/aclType';
import type { TargetableInventory } from '@openx/types/targeting/inventoryAndContent';
import type { TargetingProviderConfig } from '@openx/types/targeting/targetingProviderConfig';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { buildSearchQueryByText } from '@openx/utils/state/packages/inventories/utils';
import {
  InvPackageSearchDocument,
  type InvPackageSearchQuery,
} from '../../../../graphql/query/options/InvPackageSearch.generated';
import { InvSearchDocument, type InvSearchQuery } from '../../../../graphql/query/options/InvSearch.generated';

export const useFetchInventoriesBySearchText: TargetingProviderConfig['useFetchInventoriesBySearchText'] = ({
  accountId,
  includes,
  instanceUid,
  searchPhrase,
}) => {
  const { useIsAllowed } = usePermissionContext();
  const { useCurrentInstanceState } = useTargetingContext();
  const { isAllowed } = useIsAllowed();

  const { isBefInstance } = useCurrentInstanceState();

  const isUserAllowed =
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.ROOT_WORKAS_ANYINSTANCE)) ||
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.INTERNAL_FIELD_WRITE)) ||
    isAllowed(isAllowedByCompiledAcl(ACL_TYPE.MARKET_OPERATOR_SEARCH));

  const isBefAndRoot = isUserAllowed && isBefInstance;

  const qPhrase = buildSearchQueryByText(searchPhrase, isBefAndRoot);
  const extendQuery = getExtendQuery(includes);
  const phrase = `${qPhrase} ${extendQuery}`;

  const skip = !searchPhrase && searchPhrase.length < 1;

  const skipOp = skip || (!isBefAndRoot && !instanceUid);
  const skipPckg = skip || isBefAndRoot;

  const {
    data: { invOperatorSearch: invOperator = [] } = {},
    loading: opLoading,
    refetch: refetchOP,
  } = useQuery(InvSearchDocument, {
    skip: skipOp,
    variables: { instance_uid: `${instanceUid}`, phrase },
  });

  const {
    data: { invPackageSearch = [] } = {},
    loading: pckgLoading,
    refetch: refetchPckg,
  } = useQuery(InvPackageSearchDocument, { skip: skipPckg, variables: { account_uid: accountId, phrase } });
  const inventory: (InvSearchQuery['invOperatorSearch'][number] | InvPackageSearchQuery['invPackageSearch'][number])[] =
    [...invOperator, ...invPackageSearch];

  const data: TargetableInventory[] = inventory.map(option => ({
    account_uid: `${option.account_uid}`,
    id: option.id,
    instance_uid: 'instance_uid' in option ? option.instance_uid : undefined,
    name: option.name,
    parent_name: `${option.parent_name}`,
    type: option.type,
    type_full: `${option.type_full}`,
    uid: option.uid,
  }));
  const loading = opLoading || pckgLoading;

  const refetch = () => (isBefAndRoot ? refetchOP() : refetchPckg());

  return { data, isLoading: loading, refetch };
};
